import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useManifestsAgGridColumns, useManifestsMain } from '../../hooks';
import { ManifestsAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection } from '../interaction';
import { UploadSignedPdfModal, UploadSignedPdfModalRef } from '../modals';
import { Manifest } from '../../models';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';

const convertToTanStackColumns = (
  agGridColumns: ColDef[]
): ColumnDef<Manifest>[] =>
  agGridColumns
    .filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((column) => ({
      id: column.field || column.headerName || 'unknown',
      accessorKey: column.field || column.headerName || 'unknown',
      header: column.headerName || 'Columna',
    }));

export const Main = () => {
  const { t } = useTranslation('manifestsModule');
  const uploadModalRef = useRef<UploadSignedPdfModalRef>(null);

  const {
    manifests,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    sorting,
    selectedColumns,
    hasActiveFilters,
    fixedColumns,
    filterConditions,
    searchTerm,
    courierIntegrations,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
  } = useManifestsMain();

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const handleUploadPdf = useCallback((manifest: Manifest) => {
    uploadModalRef.current?.open(manifest);
  }, []);

  const handleDownloadExcel = useCallback((manifest: Manifest) => {
    if (manifest.excel_file_path) {
      window.open(manifest.excel_file_path, '_blank');
    }
  }, []);

  const handleViewPdf = useCallback((manifest: Manifest) => {
    if (manifest.signed_pdf_file_path) {
      window.open(manifest.signed_pdf_file_path, '_blank');
    }
  }, []);

  const handleUploadSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const { filteredColumnDefs, allColumnDefs } = useManifestsAgGridColumns({
    selectedColumns,
    courierIntegrations,
    onUploadPdf: handleUploadPdf,
    onDownloadExcel: handleDownloadExcel,
    onViewPdf: handleViewPdf,
  });

  const selectorColumns = useMemo(
    () => convertToTanStackColumns(allColumnDefs),
    [allColumnDefs]
  );

  if (isInitialLoad) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              allColumns={selectorColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              fixedColumns={fixedColumns}
              searchTerm={searchTerm}
              filterConditions={filterConditions}
              courierIntegrations={courierIntegrations}
            />
          </div>
          <div>
            <TotalResults total={total} />
          </div>
        </div>
      </div>

      {!isInitialLoad && (
        <div className="relative mb-4 mt-4">
          <div
            className={`transition-opacity duration-300 ${
              isFetching && !isFetchingNextPage
                ? 'opacity-50 pointer-events-none'
                : total > 0
                  ? 'opacity-100'
                  : 'opacity-0'
            }`}
          >
            <ManifestsAgGridTable
              manifests={manifests}
              columnDefs={filteredColumnDefs}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              isFetching={isFetching}
              sorting={sorting}
              onSortingChange={handleSortingChange}
            />
          </div>

          {total === 0 && (
            <div className="absolute inset-0">
              <EmptyView
                type={hasActiveFilters ? 'noResults' : 'emptyState'}
                icon={EMPTY_VIEW_ICONS.ORDERS}
              />
            </div>
          )}
        </div>
      )}

      <UploadSignedPdfModal ref={uploadModalRef} onSuccess={handleUploadSuccess} />
    </div>
  );
};
